import {
  Box,
  Divider,
  ScopedCssBaseline,
  Switch,
  ThemeProvider,
  Typography,
} from '@mui/material';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import emLegacyTheme from '../../theme/emLegacyTheme';
import updateHiddenField from '../../utils/updateHiddenField';
import CalendarSection from './CalendarSection';
import FileStorageSection from './FileStorageSection';
import ResourceCountSection from './ResourceCountSection';
import StorageConsumptionDisplay from './StorageConsumptionDisplay';
import translations from './translations';

translations.init();

interface ResourcesCalendarSectionProps {
  apiServerUrl?: string;
  calendar: boolean;
  calendarSharing: boolean;
  customerUuid?: string;
  fileStorageLimitMb: number;
  resourceCountLimit: number;
  resourceManagement: boolean;
}

const updateHiddenFieldWithChange = (
  fieldId: string,
  value: boolean | null | number | object | string,
) => {
  updateHiddenField(fieldId, value);
  const element = document.getElementById(fieldId);
  if (element) {
    element.dispatchEvent(new Event('change', { bubbles: true }));
  }
};

interface StorageUsage {
  limitBytes: number;
  usedBytes: number;
}

const ResourcesCalendarSection: FC<ResourcesCalendarSectionProps> = ({
  apiServerUrl,
  calendar: initialCalendar,
  calendarSharing: initialCalendarSharing,
  customerUuid,
  fileStorageLimitMb: initialFileStorageLimitMb,
  resourceCountLimit: initialResourceCountLimit,
  resourceManagement: initialResourceManagement,
}) => {
  const { t } = useTranslation();
  const [resourceManagement, setResourceManagement] = useState(
    initialResourceManagement,
  );
  const [calendar, setCalendar] = useState(initialCalendar);
  const [calendarSharing, setCalendarSharing] = useState(
    initialCalendarSharing,
  );
  const [resourceCountLimit, setResourceCountLimit] = useState(
    initialResourceCountLimit ?? 250,
  );
  const [fileStorageLimitMb, setFileStorageLimitMb] = useState(
    initialFileStorageLimitMb ?? 250,
  );
  const [storageUsage, setStorageUsage] = useState<StorageUsage | null>(null);

  useEffect(() => {
    if (!customerUuid || !apiServerUrl) return;

    const fetchStorageUsage = async () => {
      try {
        const response = await fetch(
          `${apiServerUrl}/v1/customers/${customerUuid}/limits`,
          { credentials: 'include' },
        );
        if (response.ok) {
          const data = await response.json();
          setStorageUsage({
            limitBytes: data.file_storage.limit,
            usedBytes: data.file_storage.used,
          });
        }
      } catch {
        // Silently fail — consumption display is informational
      }
    };

    fetchStorageUsage();
  }, [customerUuid, apiServerUrl]);

  const handleMasterToggle = (checked: boolean) => {
    setResourceManagement(checked);
    updateHiddenFieldWithChange('customer_resource_management', checked);
    if (!checked) {
      setCalendar(false);
      setCalendarSharing(false);
      const resetCount = initialResourceCountLimit ?? 250;
      setResourceCountLimit(resetCount);
      updateHiddenFieldWithChange('customer_calendar', false);
      updateHiddenFieldWithChange('customer_calendar_sharing', false);
      updateHiddenFieldWithChange('customer_resource_count_limit', resetCount);
    }
  };

  const handleResourceCountChange = (value: number) => {
    setResourceCountLimit(value);
    updateHiddenFieldWithChange('customer_resource_count_limit', value);
  };

  const handleFileStorageLimitChange = (valueMb: number) => {
    setFileStorageLimitMb(valueMb);
    updateHiddenFieldWithChange(
      'customer_file_storage_limit_megabytes',
      valueMb,
    );
  };

  const handleCalendarChange = (value: boolean) => {
    setCalendar(value);
    updateHiddenFieldWithChange('customer_calendar', value);
    if (!value && calendarSharing) {
      setCalendarSharing(false);
      updateHiddenFieldWithChange('customer_calendar_sharing', false);
    }
  };

  const handleCalendarSharingChange = (value: boolean) => {
    setCalendarSharing(value);
    updateHiddenFieldWithChange('customer_calendar_sharing', value);
  };

  return (
    <ThemeProvider theme={emLegacyTheme}>
      <ScopedCssBaseline />
      <Box padding={5}>
        <Box gap={3} sx={{ alignItems: 'center', display: 'flex' }}>
          <Typography
            color="primary"
            sx={{ fontSize: '13px', textTransform: 'uppercase' }}
            variant="text-xs-bold-caps"
          >
            {t('calendar-and-resources')}
          </Typography>
          <Switch
            checked={resourceManagement}
            onChange={(e) => handleMasterToggle(e.target.checked)}
          />
        </Box>
        <ResourceCountSection
          disabled={!resourceManagement}
          onChange={handleResourceCountChange}
          value={resourceCountLimit}
        />
        <Divider sx={{ my: 5 }} />
        <FileStorageSection
          disabled={!resourceManagement}
          fileStorageLimitMb={fileStorageLimitMb}
          onFileStorageLimitChange={handleFileStorageLimitChange}
        />
        {storageUsage && (
          <StorageConsumptionDisplay
            limitBytes={storageUsage.limitBytes}
            usedBytes={storageUsage.usedBytes}
          />
        )}
        <Divider sx={{ my: 5 }} />
        <CalendarSection
          calendar={calendar}
          calendarSharing={calendarSharing}
          disabled={!resourceManagement}
          onCalendarChange={handleCalendarChange}
          onCalendarSharingChange={handleCalendarSharingChange}
        />
      </Box>
    </ThemeProvider>
  );
};

export default ResourcesCalendarSection;
